import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsProps } from 'antd';
import AuthService from 'core/auth/auth.service';
import {
  CategoriaNoteDto,
  OrderCategoryDto,
  OrderTypeDto,
  OrderTypologyDto,
  ProjectStatusDto,
  ProjectTypeDto,
  RuoloAccessoOutputDto,
  UtenteRuoloAccessoOutputDto,
} from '@api/client';
import SimpleCrudTab from './_components/simple-crud-tab';
import ImpersonationTab from './_components/impersonation-tab';
import { useAppDispatch } from 'core/store/hooks';
import { authStartImpersonation } from 'pages/auth/_redux/actions';
import {
  deleteNoteCategory,
  deleteOrderCategory,
  deleteOrderType,
  deleteOrderTypology,
  deleteProjectStatus,
  deleteProjectType,
  deleteRuoloAccesso,
  deleteUtenteRuoloAccesso,
  getNoteCategories,
  getOrderCategoriesList,
  getOrderTypesList,
  getOrderTypologiesList,
  getProjectStatusesList,
  getProjectTypesList,
  getRuoloAccessoList,
  getUsersList,
  getUtenteRuoloAccessoList,
  saveNoteCategory,
  saveOrderCategory,
  saveOrderType,
  saveOrderTypology,
  saveProjectStatus,
  saveProjectType,
  saveRuoloAccesso,
  saveUtenteRuoloAccesso,
} from './_redux/actions';

interface UtenteRuoloAccessoRow extends UtenteRuoloAccessoOutputDto {
  ruoloAccessoId?: number;
}

const Settings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isSuperAdmin = AuthService.isSuperAdmin();
  const [ruoloAccessoOptions, setRuoloAccessoOptions] = useState<{ value: number; label: string }[]>([]);
  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>([]);

  useEffect(() => {
    dispatch(getRuoloAccessoList())
      .unwrap()
      .then((res) => setRuoloAccessoOptions((res ?? []).map((r) => ({ value: r.id!, label: r.nome ?? '' }))));
    dispatch(getUsersList())
      .unwrap()
      .then((res) =>
        setUserOptions(
          (res ?? [])
            .filter((u) => !!u.email)
            .map((u) => ({ value: u.email!, label: u.nominativo ? `${u.nominativo} (${u.email})` : u.email! })),
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isSuperAdmin) {
    return <div className="p-4">{t('Non hai i permessi per accedere a questa pagina')}</div>;
  }

  const items: TabsProps['items'] = [
    {
      key: 'note-categories',
      label: t('Categorie note'),
      children: (
        <SimpleCrudTab<CategoriaNoteDto>
          fetchAll={() =>
            dispatch(getNoteCategories())
              .unwrap()
              .then((res) => res?.categoriaNoteDtoList ?? [])
          }
          save={(item) => dispatch(saveNoteCategory(item)).unwrap()}
          remove={(id) => dispatch(deleteNoteCategory(id)).unwrap()}
          fields={[{ key: 'title', label: t('Titolo'), required: true }]}
          emptyMessage={t('Nessuna categoria nota')}
          addLabel={t('Aggiungi categoria')}
          fileName="categorie-note"
        />
      ),
    },
    {
      key: 'project-types',
      label: t('Tipi di progetto'),
      children: (
        <SimpleCrudTab<ProjectTypeDto>
          fetchAll={() => dispatch(getProjectTypesList()).unwrap().then((res) => res ?? [])}
          save={(item) => dispatch(saveProjectType(item)).unwrap()}
          remove={(id) => dispatch(deleteProjectType(id)).unwrap()}
          fields={[{ key: 'value', label: t('Nome'), required: true }]}
          emptyMessage={t('Nessun tipo di progetto')}
          addLabel={t('Aggiungi tipo')}
          fileName="tipi-progetto"
        />
      ),
    },
    {
      key: 'project-statuses',
      label: t('Stati di progetto'),
      children: (
        <SimpleCrudTab<ProjectStatusDto>
          fetchAll={() => dispatch(getProjectStatusesList()).unwrap().then((res) => res ?? [])}
          save={(item) => dispatch(saveProjectStatus(item)).unwrap()}
          remove={(id) => dispatch(deleteProjectStatus(id)).unwrap()}
          fields={[
            { key: 'value', label: t('Nome'), required: true },
            { key: 'statusDescription', label: t('Descrizione'), type: 'textarea' },
          ]}
          emptyMessage={t('Nessuno stato di progetto')}
          addLabel={t('Aggiungi stato')}
          fileName="stati-progetto"
        />
      ),
    },
    {
      key: 'order-typologies',
      label: t('Tipologie ordini'),
      children: (
        <SimpleCrudTab<OrderTypologyDto>
          fetchAll={() => dispatch(getOrderTypologiesList()).unwrap().then((res) => res ?? [])}
          save={(item) => dispatch(saveOrderTypology(item)).unwrap()}
          remove={(id) => dispatch(deleteOrderTypology(id)).unwrap()}
          fields={[{ key: 'value', label: t('Nome'), required: true }]}
          emptyMessage={t('Nessuna tipologia di ordine')}
          addLabel={t('Aggiungi tipologia')}
          fileName="tipologie-ordini"
        />
      ),
    },
    {
      key: 'order-types',
      label: t('Tipi ordini'),
      children: (
        <SimpleCrudTab<OrderTypeDto>
          fetchAll={() => dispatch(getOrderTypesList()).unwrap().then((res) => res ?? [])}
          save={(item) => dispatch(saveOrderType(item)).unwrap()}
          remove={(id) => dispatch(deleteOrderType(id)).unwrap()}
          fields={[{ key: 'value', label: t('Nome'), required: true }]}
          emptyMessage={t('Nessun tipo di ordine')}
          addLabel={t('Aggiungi tipo')}
          fileName="tipi-ordini"
        />
      ),
    },
    {
      key: 'order-categories',
      label: t('Categorie ordini'),
      children: (
        <SimpleCrudTab<OrderCategoryDto>
          fetchAll={() => dispatch(getOrderCategoriesList()).unwrap().then((res) => res ?? [])}
          save={(item) => dispatch(saveOrderCategory(item)).unwrap()}
          remove={(id) => dispatch(deleteOrderCategory(id)).unwrap()}
          fields={[
            { key: 'value', label: t('Nome'), required: true },
            {
              key: 'isTypePassivo',
              label: t('Tipologia'),
              type: 'boolean',
              trueLabel: t('Passivo'),
              falseLabel: t('Attivo'),
            },
          ]}
          filterFields={[
            { key: 'value', label: t('Cerca per nome') },
            {
              key: 'isTypePassivo',
              label: t('Tipologia'),
              type: 'boolean',
              trueLabel: t('Passivo'),
              falseLabel: t('Attivo'),
            },
          ]}
          emptyMessage={t('Nessuna categoria di ordine')}
          addLabel={t('Aggiungi categoria')}
          fileName="categorie-ordini"
        />
      ),
    },
    {
      key: 'ruolo-accesso',
      label: t('Ruolo Accesso'),
      children: (
        <SimpleCrudTab<RuoloAccessoOutputDto>
          fetchAll={() => dispatch(getRuoloAccessoList()).unwrap().then((res) => res ?? [])}
          save={(item) => dispatch(saveRuoloAccesso(item)).unwrap()}
          remove={(id) => dispatch(deleteRuoloAccesso(id)).unwrap()}
          fields={[
            { key: 'nome', label: t('Nome'), required: true, placeholder: t('Inserisci il nome del ruolo') },
            { key: 'codice', label: t('Codice'), required: true, placeholder: t('Inserisci il codice del ruolo') },
          ]}
          filterFields={[{ key: 'nome', label: t('Cerca per nome'), placeholder: t('Cerca per nome ruolo') }]}
          emptyMessage={t('Nessun ruolo di accesso')}
          addLabel={t('Aggiungi ruolo')}
          fileName="ruoli-accesso"
        />
      ),
    },
    {
      key: 'utente-ruolo-accesso',
      label: t('Associazioni Utente-Ruolo'),
      children: (
        <SimpleCrudTab<UtenteRuoloAccessoRow>
          fetchAll={() =>
            dispatch(getUtenteRuoloAccessoList())
              .unwrap()
              .then((res) => (res ?? []).map((r) => ({ ...r, ruoloAccessoId: r.ruoloAccesso?.id })))
          }
          save={(item) =>
            dispatch(
              saveUtenteRuoloAccesso({ id: item.id, utente: item.utente, ruoloAccessoId: item.ruoloAccessoId }),
            )
              .unwrap()
              .then((res) => (res ? { ...res, ruoloAccessoId: res.ruoloAccesso?.id } : undefined))
          }
          remove={(id) => dispatch(deleteUtenteRuoloAccesso(id)).unwrap()}
          fields={[
            {
              key: 'utente',
              label: t('Utente'),
              type: 'select',
              required: true,
              options: userOptions,
              placeholder: t('Cerca utente per nome o email...'),
            },
            {
              key: 'ruoloAccessoId',
              label: t('Ruolo'),
              type: 'select',
              required: true,
              options: ruoloAccessoOptions,
              placeholder: t('Cerca ruolo per nome...'),
              // Mostra sempre il nome ruolo già associato alla riga (dato annidato restituito dalla API),
              // senza dipendere dal caricamento asincrono di `ruoloAccessoOptions`.
              render: (item: UtenteRuoloAccessoRow) => item.ruoloAccesso?.nome ?? '-',
            },
          ]}
          filterFields={[
            { key: 'utente', label: t('Cerca per utente'), placeholder: t('Cerca per email utente') },
          ]}
          emptyMessage={t('Nessuna associazione utente-ruolo')}
          addLabel={t('Aggiungi associazione')}
          fileName="associazioni-utente-ruolo"
        />
      ),
    },
    {
      key: 'impersonation',
      label: t('Impersonazione'),
      children: (
        <ImpersonationTab
          userOptions={userOptions}
          onStart={(targetUsername, reason) =>
            dispatch(authStartImpersonation({ targetUsername, reason })).unwrap()
          }
        />
      ),
    },
  ];

  return (
    <>
      <h4 className="text-gray-800 my-3">{t('Settings')}</h4>
      <Tabs defaultActiveKey="note-categories" items={items} />
    </>
  );
};

export default Settings;
